import { memo, useMemo } from 'react';
import type { GameSessionHandle } from '@sorrem-client/game/game-session-store';
import { Button, ErrorBoundary } from '@sorrem-client/ui/components';
import { GameOverlay } from '@sorrem-client/ui/features/GameOverlay';
import { useLeaveRun } from './use-leave-run';
import { GameplayContext, type GameplayContextValue } from './gameplay-context';
import styles from './PlayPage.module.scss';

interface RunViewProps {
    /** The session whose run is under way (its canvas is already mounted). */
    readonly session: GameSessionHandle;
    /** Where the run's loading bar starts, 0 to 1 (see `GameOverlay`). */
    readonly loadingProgressStart?: number;
    /** The party leader's account (see `GameOverlay`). */
    readonly partyLeaderAccountId?: string | null;
}

/**
 * The UI over a run: the gameplay HUD, with a fallback that returns the
 * player to their character if it crashes. Shown by the crucible page once
 * the party enters.
 */
export const RunView = memo(function RunView({ session, loadingProgressStart, partyLeaderAccountId }: RunViewProps): JSX.Element {
    const leaveRun = useLeaveRun();
    // One value per session: a new one would re-render every HUD part that reads it.
    const gameplay = useMemo((): GameplayContextValue => ({
        bridge: session.bridge,
        crucibleId: session.crucibleId,
        ...session.gameplayApi,
    }), [session]);

    return (
        <div
            className={styles.playPage}
            onContextMenu={(event) => event.preventDefault()}
        >
            <GameplayContext.Provider value={gameplay}>
                <ErrorBoundary
                    label="Gameplay"
                    fallback={(error, reset) => (
                        <div className={styles.gameplayCrashFallback} role="alert">
                            <p className={styles.gameplayCrashTitle}>The game session hit an unexpected error.</p>
                            <p className={styles.gameplayCrashMessage}>{error.message}</p>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    reset();
                                    void leaveRun('home', { replace: true });
                                }}
                            >
                                Return to Character
                            </Button>
                        </div>
                    )}
                >
                    <GameOverlay loadingProgressStart={loadingProgressStart} partyLeaderAccountId={partyLeaderAccountId} />
                </ErrorBoundary>
            </GameplayContext.Provider>
        </div>
    );
});
